import { MAX_NIKOLA_MESSAGE_PARTS, type NikolaMessagePart } from "@chessla/shared";
import { parseBoardMarkers, type BoardSegment } from "../board/parse-board-markers";
import type { PendingAction } from "./chat.api";

export type RenderableNikolaPart = NikolaMessagePart | { type: "board-marker"; board: BoardSegment };
export const NIKOLA_ANALYSIS_PLACEHOLDER = "__analysis_placeholder__";
export const expandNikolaMessageParts = (parts: readonly NikolaMessagePart[]): readonly RenderableNikolaPart[] => {
  const expanded: RenderableNikolaPart[] = [];
  for (const part of parts) {
    if (part.type !== "text") {
      expanded.push(part);
      continue;
    }
    if (part.markdown === NIKOLA_ANALYSIS_PLACEHOLDER) continue;
    for (const segment of parseBoardMarkers(part.markdown).segments) {
      if (segment.type === "board") expanded.push({ type: "board-marker", board: segment });
      else if (segment.content) expanded.push({ type: "text", markdown: segment.content });
    }
  }
  return expanded;
};

export const hasActionPart = (parts: readonly NikolaMessagePart[], pending?: PendingAction | null): boolean =>
  parts.some(
    (part) =>
      part.type === "actions" &&
      part.actions.some(
        (action) =>
          !pending ||
          (action.type === pending.type && (action.gameId ?? action.fen) === (pending.gameId ?? pending.fen)),
      ),
  );

export const completeNikolaActivityParts = (parts: readonly NikolaMessagePart[] = []): readonly NikolaMessagePart[] =>
  parts.map((part) =>
    part.type === "activity" && part.activity.status === "running"
      ? { ...part, activity: { ...part.activity, status: "complete" } }
      : part,
  );

export const mergeNikolaActivityParts = (
  parts: readonly NikolaMessagePart[] = [],
  activity: Extract<NikolaMessagePart, { type: "activity" }>["activity"],
): readonly NikolaMessagePart[] => {
  type TimelineActivity = typeof activity & { activityId?: string; occurredAt?: string };
  const incoming = activity as TimelineActivity;
  const next = { type: "activity" as const, activity };
  const content = parts.filter((part) => part.type !== "activity");
  const timeline = parts
    .filter((part): part is Extract<NikolaMessagePart, { type: "activity" }> => part.type === "activity")
    .filter((part) => {
      const existing = part.activity as TimelineActivity;
      return !incoming.activityId || existing.activityId !== incoming.activityId;
    });
  timeline.push(next);
  timeline.sort((left, right) => {
    const leftSequence = (left.activity as TimelineActivity).sequence;
    const rightSequence = (right.activity as TimelineActivity).sequence;
    if (leftSequence !== undefined && rightSequence !== undefined) return leftSequence - rightSequence;
    const leftAt = Date.parse((left.activity as TimelineActivity).occurredAt ?? "");
    const rightAt = Date.parse((right.activity as TimelineActivity).occurredAt ?? "");
    return Number.isNaN(leftAt) || Number.isNaN(rightAt) ? 0 : leftAt - rightAt;
  });
  const boundedContent = content.slice(-MAX_NIKOLA_MESSAGE_PARTS);
  const activityLimit = MAX_NIKOLA_MESSAGE_PARTS - boundedContent.length;
  const boundedTimeline = activityLimit > 0 ? timeline.slice(-activityLimit) : [];
  return [...boundedContent, ...boundedTimeline];
};
